import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';import {
  MapPin, Briefcase, GraduationCap, Star, Calendar, Globe,
  ArrowLeft, ExternalLink, ScanSearch, X, ChevronRight, ChevronLeft,
  Instagram, Linkedin, Award, TrendingUp, Users, Eye,
  Layers, Zap, BarChart2, FileText, Shield, Check,
  BookmarkPlus, HelpCircle, ChevronDown, ChevronUp,
  ImageOff, Maximize2, Grid3X3, Target, Send, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import AIBrief from '../components/AIBriefPanel';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONO = { fontFamily: "'IBM Plex Mono', monospace" };
const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };

// ── CV Viewer (protected — contact info redacted) ─────────────────────────────
function CvViewerButton({ candidateId }) {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);

  const openCV = async () => {
    setOpen(true);
    if (html) return;
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/candidates/${candidateId}/cv-view`, {});
      const text = await r.text();
      setHtml(text);
    } catch {
      setHtml('<p style="color:#999;padding:24px">Could not load CV.</p>');
    }
    setLoading(false);
  };

  return (
    <>
      <button data-testid="cv-view-button" onClick={openCV}
        className="flex items-center gap-2.5 text-sm text-foreground hover:text-[hsl(var(--primary))] transition-colors font-medium">
        <FileText className="w-3.5 h-3.5 flex-shrink-0 text-[hsl(var(--primary))]" strokeWidth={1.5} />
        View Protected CV
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[hsl(var(--primary))]" />
                <span className="text-sm font-semibold">Protected CV View</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-muted-foreground bg-secondary px-2 py-0.5 uppercase tracking-widest">Contact info redacted</span>
                <button data-testid="cv-modal-close" onClick={() => setOpen(false)}
                  className="text-muted-foreground hover:text-foreground text-lg leading-none">×</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <p style={{ fontFamily: "'IBM Plex Mono', monospace" }} className="text-xs text-muted-foreground uppercase tracking-widest">
                    Loading CV...
                  </p>
                </div>
              ) : (
                <iframe
                  data-testid="cv-iframe"
                  srcDoc={html}
                  className="w-full h-full min-h-[500px] border-none"
                  title="Protected CV"
                  sandbox="allow-same-origin"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Score Tooltip Component ───────────────────────────────────────────────────
const SCORE_DESCRIPTIONS = {
  'Role Fit': {
    title: 'Role Fit (contextual)',
    desc: 'How well this candidate fits a specific role — scored relative to the role, seniority and skills required. The same candidate scores high for their core discipline and lower for a mismatched role. Defaults to their best-fit role; type any role to re-score.',
  },
  Overall: {
    title: 'Overall Intelligence Score',
    desc: 'FashionSpyder\'s composite score (0–10) combining Skill DNA, Cultural Knowledge, Professional Authority, and Trend Alignment. Computed by FashionSpyder — not Flence\'s ranking.',
  },
  'Trend Align.': {
    title: 'Trend Alignment',
    desc: 'How well the candidate\'s aesthetic sensibility aligns with current and emerging fashion trends. Based on AI analysis of their portfolio and cultural metadata.',
  },
  Authority: {
    title: 'Professional Authority',
    desc: 'Professional weight and industry credibility (0–10). Primarily driven by career experience and seniority level, with bonuses for skills richness and platform engagement. Senior designers will always show a meaningful authority score.',
  },
};

const ScoreTooltip = ({ label }) => {
  const [open, setOpen] = useState(false);
  const info = SCORE_DESCRIPTIONS[label];
  if (!info) return null;
  return (
    <div className="relative inline-block ml-1">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="text-muted-foreground/40 hover:text-muted-foreground transition-colors"
        data-testid={`score-tooltip-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <HelpCircle className="w-3 h-3" strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-64 bg-foreground text-background p-3 shadow-lg pointer-events-none"
          style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', lineHeight: 1.6 }}>
          <p className="font-semibold uppercase tracking-wider mb-1">{info.title}</p>
          <p className="text-background/70">{info.desc}</p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-foreground" />
        </div>
      )}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.15em' }}
    className="uppercase text-muted-foreground">{children}</p>
);

// ── Helpers ──────────────────────────────────────────────────────────────────
const parseCultural = (meta) => {
  if (!meta) return {};
  if (typeof meta === 'string') { try { return JSON.parse(meta); } catch { return {}; } }
  return meta;
};

const computeSkillDNA = (candidate) => {
  const all = [...(candidate.software_skills || []), ...(candidate.manual_creative_skills || [])];
  if (!all.length) return 0;
  const avg = all.reduce((s, x) => s + (x.level || 0), 0) / all.length;
  return Math.round((avg / 5) * 100);
};

const computeCulturalDNA = (cm) => {
  const tags = [
    ...(cm.historian_tags || []),
    ...(cm.designer_archetypes || []),
    ...(cm.subculture_vibe || []),
  ];
  if (!tags.length) return 0;
  return Math.min(96, 18 + tags.length * 10);
};

const formatCulturalTag = (tag) => {
  if (!tag) return '';
  return tag.replace(/-/g, ' · ').toUpperCase();
};

// ── Score Bar Component ───────────────────────────────────────────────────────
const DNABar = ({ label, score, sublabel, icon: Icon, animate }) => {
  const safeScore = score || 0;
  return (
    <div className="space-y-3" data-testid={`dna-bar-${label.toLowerCase().replace(' ', '-')}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />}
          <span style={{ ...MONO, fontSize: '10px', letterSpacing: '0.12em' }}
            className="uppercase text-muted-foreground">{label}</span>
        </div>
        <span style={{ ...MONO, fontSize: '22px' }} className="font-medium text-foreground leading-none">
          {safeScore}
          <span style={{ fontSize: '11px' }} className="text-muted-foreground ml-0.5">%</span>
        </span>
      </div>
      <div className="h-px bg-border relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-foreground transition-all duration-1000 ease-out"
          style={{ width: animate ? `${safeScore}%` : '0%', transitionDelay: '200ms' }}
        />
      </div>
      {sublabel && (
        <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }}
          className="text-muted-foreground/60 uppercase">{sublabel}</p>
      )}
    </div>
  );
};

// ── Skill Dot Component ───────────────────────────────────────────────────────
const SkillRow = ({ name, level, animate }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
    <span className="text-sm text-foreground">{name}</span>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <div
          key={i}
          className="w-4 h-1 transition-colors duration-500"
          style={{
            transitionDelay: animate ? `${i * 80}ms` : '0ms',
            backgroundColor: i <= level ? 'hsl(var(--foreground))' : 'hsl(var(--border))',
          }}
        />
      ))}
    </div>
  </div>
);

// ── Cultural Tag Chip ─────────────────────────────────────────────────────────
const CulturalChip = ({ tag, variant = 'primary' }) => {
  const styles = {
    primary: 'border-foreground/40 text-foreground',
    secondary: 'border-border text-muted-foreground',
    gold: 'border-yellow-600/40 text-yellow-700',
  };
  return (
    <span
      className={`inline-flex items-center border px-2.5 py-1 ${styles[variant]}`}
      style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
    >
      {formatCulturalTag(tag)}
    </span>
  );
};

// ── Portfolio Lightbox ────────────────────────────────────────────────────────
// Detects if a URL is a video (mov, mp4, webm)
const isVideo = (url = '') => /\.(mov|mp4|webm|avi|m4v)(\?|$)/i.test(url);

// Get all displayable media for a project (cover + any additional images)
function getProjectMedia(item) {
  const additional = (item.images || []).filter(Boolean);
  // If cover already included in images array, don't duplicate
  const coverAlreadyIn = additional.some(u => u === item.cover_image);
  return coverAlreadyIn ? additional : [item.cover_image, ...additional].filter(Boolean);
}

function PortfolioLightbox({ projects, initialProjectIndex, onClose }) {
  const [projectIdx, setProjectIdx] = useState(initialProjectIndex);
  const [imageIdx, setImageIdx] = useState(0);
  const [mediaFailed, setMediaFailed] = useState({});   // key: `${projectIdx}-${imageIdx}`

  const project = projects[projectIdx];
  const media = getProjectMedia(project);
  const totalImages = media.length;
  const totalProjects = projects.length;
  const mediaUrl = media[imageIdx] || '';
  const failKey = `${projectIdx}-${imageIdx}`;
  const isFailed = mediaFailed[failKey];
  const isVid = isVideo(mediaUrl);

  // Reset image index when switching projects
  useEffect(() => { setImageIdx(0); }, [projectIdx]);

  // Keyboard navigation within images of current project
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowRight') setImageIdx(i => Math.min(i + 1, totalImages - 1));
      if (e.key === 'ArrowLeft')  setImageIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, totalImages]);

  const goNextImage = (e) => { e.stopPropagation(); setImageIdx(i => Math.min(i + 1, totalImages - 1)); };
  const goPrevImage = (e) => { e.stopPropagation(); setImageIdx(i => Math.max(i - 1, 0)); };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black flex flex-col"
      data-testid="portfolio-lightbox"
    >
      {/* ── Top bar ── */}
      <div className="flex items-start justify-between px-6 pt-5 pb-3 flex-shrink-0">
        <div className="min-w-0 flex-1">
          {/* Project navigation chips */}
          {totalProjects > 1 && (
            <div className="flex items-center gap-2 mb-2 overflow-x-auto pb-1">
              {projects.map((p, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setProjectIdx(i); }}
                  style={{ ...MONO, fontSize: '8px', letterSpacing: '0.1em' }}
                  className={`flex-shrink-0 px-2.5 py-1 uppercase border transition-colors ${
                    i === projectIdx
                      ? 'border-white text-white bg-white/10'
                      : 'border-white/20 text-white/40 hover:border-white/50 hover:text-white/60'
                  }`}
                  data-testid={`lightbox-project-tab-${i}`}
                >
                  {i + 1}. {p.title.slice(0, 20)}{p.title.length > 20 ? '…' : ''}
                </button>
              ))}
            </div>
          )}
          <h2 style={{ ...SERIF, fontSize: 'clamp(1rem, 3vw, 1.6rem)', lineHeight: 1.1 }}
            className="text-white font-light truncate pr-4">
            {project.title}
          </h2>
          <div className="flex items-center gap-3 mt-1">
            <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }}
              className="text-white/40 uppercase">
              {imageIdx + 1} / {totalImages} {totalImages > 1 ? 'images' : 'image'}
            </span>
            {project.views > 0 && (
              <span style={{ ...MONO, fontSize: '9px' }} className="text-white/30 flex items-center gap-1">
                <Eye className="w-3 h-3" strokeWidth={1.5} />
                {project.views.toLocaleString()} views
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white/50 hover:text-white transition-colors ml-4 mt-1 flex-shrink-0"
          data-testid="lightbox-close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* ── Main media area ── */}
      <div className="relative flex-1 min-h-0 bg-zinc-950 mx-6 mb-3">
        {isFailed || !mediaUrl ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <ImageOff className="w-14 h-14 text-white/10" strokeWidth={1} />
            <div className="text-center">
              <p style={{ ...MONO, fontSize: '10px', letterSpacing: '0.15em' }} className="text-white/30 uppercase">
                Image not available
              </p>
              <p style={{ ...MONO, fontSize: '9px' }} className="text-white/20 mt-1">
                The creator hasn't uploaded this file yet
              </p>
            </div>
          </div>
        ) : isVid ? (
          <video
            key={mediaUrl}
            src={mediaUrl}
            className="w-full h-full object-contain"
            controls
            autoPlay
            playsInline
            onError={() => setMediaFailed(prev => ({ ...prev, [failKey]: true }))}
            data-testid="lightbox-video"
          />
        ) : (
          <img
            key={mediaUrl}
            src={mediaUrl}
            alt={project.title}
            className="w-full h-full object-contain"
            onError={() => setMediaFailed(prev => ({ ...prev, [failKey]: true }))}
            data-testid="lightbox-image"
          />
        )}

        {/* Prev / Next image arrows */}
        {totalImages > 1 && (
          <>
            <button
              onClick={goPrevImage}
              disabled={imageIdx === 0}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 hover:bg-black/90 flex items-center justify-center text-white transition-colors disabled:opacity-20"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              onClick={goNextImage}
              disabled={imageIdx === totalImages - 1}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 hover:bg-black/90 flex items-center justify-center text-white transition-colors disabled:opacity-20"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </>
        )}
      </div>

      {/* ── Thumbnail filmstrip — images within the current project ── */}
      {totalImages > 1 && (
        <div className="flex-shrink-0 px-6 pb-5">
          <div className="flex gap-1.5 overflow-x-auto">
            {media.map((url, i) => {
              const tFailed = mediaFailed[`${projectIdx}-${i}`];
              const isV = isVideo(url);
              return (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setImageIdx(i); }}
                  className={`flex-shrink-0 w-14 h-14 overflow-hidden transition-all border-2 ${
                    i === imageIdx ? 'border-white opacity-100' : 'border-transparent opacity-35 hover:opacity-65'
                  }`}
                  data-testid={`lightbox-thumb-${i}`}
                >
                  {tFailed ? (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                      <span style={{ ...MONO, fontSize: '8px' }} className="text-white/30">{i + 1}</span>
                    </div>
                  ) : isV ? (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white/60">
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={`${i + 1}`}
                      className="w-full h-full object-cover"
                      onError={() => setMediaFailed(prev => ({ ...prev, [`${projectIdx}-${i}`]: true }))}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Keyboard hint ── */}
      {totalImages === 1 && (
        <div className="flex-shrink-0 px-6 pb-5">
          <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.08em' }} className="text-white/15 uppercase">
            ESC to close{totalProjects > 1 ? ' · Click project tabs to switch' : ''}
          </p>
        </div>
      )}
      {totalImages > 1 && (
        <div className="flex-shrink-0 px-6 pb-1">
          <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.08em' }} className="text-white/15 uppercase">
            ← → navigate images · ESC close
          </p>
        </div>
      )}

      {/* Description if available */}
      {project.description && (
        <div className="flex-shrink-0 px-6 pb-4">
          <p className="text-sm text-white/40 leading-relaxed max-w-2xl">{project.description}</p>
        </div>
      )}
    </div>
  );
}

// ── Portfolio Grid (full section) ─────────────────────────────────────────────
function PortfolioSection({ portfolio, onOpenLightbox }) {
  const [imgFailed, setImgFailed] = useState({});
  if (!portfolio || portfolio.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-0" data-testid="portfolio-section-full">
      <div className="border border-border">
        <div className="px-4 sm:px-8 py-4 sm:py-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Grid3X3 className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <span style={{ ...MONO, fontSize: '10px', letterSpacing: '0.18em' }}
              className="uppercase text-foreground font-medium">Portfolio</span>
          </div>
          <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
            className="text-muted-foreground uppercase">{portfolio.length} project{portfolio.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Image grid — responsive: 2 col mobile, 3 col sm, 4 col lg */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 divide-x divide-y divide-border">
          {portfolio.map((item, i) => (
            <button
              key={i}
              onClick={() => onOpenLightbox(i)}
              className="group relative overflow-hidden bg-secondary text-left"
              style={{ aspectRatio: '3/4' }}
              data-testid={`portfolio-grid-item-${i}`}
            >
              {imgFailed[i] ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[hsl(0,0%,96%)]">
                  <ImageOff className="w-6 h-6 text-muted-foreground/30" strokeWidth={1} />
                  <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.08em' }}
                    className="text-muted-foreground/40 uppercase px-2 text-center leading-tight">
                    Not uploaded
                  </p>
                </div>
              ) : isVideo(item.cover_image) ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-zinc-900 group-hover:bg-zinc-800 transition-colors">
                  {/* Play button ring */}
                  <div className="relative flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full border-2 border-white/30 group-hover:border-white/60 transition-colors flex items-center justify-center bg-white/5 group-hover:bg-white/10">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white/70 group-hover:fill-white transition-colors ml-0.5" style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))' }}>
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                    </div>
                  </div>
                  <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.12em' }}
                    className="text-white/40 group-hover:text-white/60 uppercase transition-colors">Video</p>
                </div>
              ) : (
                <img
                  src={item.cover_image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                  style={{ filter: 'grayscale(0.2)' }}
                  onError={() => setImgFailed(prev => ({ ...prev, [i]: true }))}
                  loading="lazy"
                />
              )}

              {/* Hover overlay with title + expand icon */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex flex-col items-center justify-end p-3">
                <div className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300 w-full">
                  <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.08em' }}
                    className="text-white uppercase leading-tight mb-1 line-clamp-2">
                    {item.title}
                  </p>
                  {item.views > 0 && (
                    <p style={{ ...MONO, fontSize: '8px' }} className="text-white/60">
                      {item.views.toLocaleString()} views
                    </p>
                  )}
                </div>
                <Maximize2 className="absolute top-3 right-3 w-3.5 h-3.5 text-white opacity-0 group-hover:opacity-70 transition-opacity" strokeWidth={1.5} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Profile Intelligence Guide (collapsible) ─────────────────────────────────
const GUIDE_STEPS = [
  {
    icon: Award,
    title: 'Overall Score (0–10)',
    desc: 'FashionSpyder\'s composite intelligence score. Combines Skill DNA (35%), Cultural DNA (30%), Authority (20%) and Trend Alignment (15%). Unlike Flence\'s ranking, this score is calibrated by FashionSpyder and reflects deeper career signals.',
  },
  {
    icon: BarChart2,
    title: 'Authority Score',
    desc: 'Professional weight and industry credibility. Primarily based on seniority and years of experience — a Lead with 10+ years will always score high, even on a new platform. Platform engagement (views, endorsements) adds bonus points.',
  },
  {
    icon: TrendingUp,
    title: 'Trend Alignment',
    desc: 'How aligned the candidate\'s aesthetic is with current and emerging fashion trends. Derived from their cultural metadata, portfolio analysis, and AI-mapped trend keywords.',
  },
  {
    icon: Layers,
    title: 'Skill DNA',
    desc: 'Depth and breadth of technical and creative skills. Combines skill count, average proficiency level, and years of experience. A candidate with 8+ skills at high levels will score 90%+.',
  },
  {
    icon: Users,
    title: 'Cultural DNA',
    desc: 'Aesthetic identity and cultural depth. Scored from historian era references, designer archetypes, and subculture signatures. Reflects how culturally specific and fashion-literate the candidate is.',
  },
  {
    icon: FileText,
    title: 'CV — Protected View',
    desc: 'CV text is extracted and displayed with all contact details redacted (email, phone, social handles). Use Messages to initiate contact. All communication stays on-platform per Flence\'s privacy rules.',
  },
  {
    icon: ScanSearch,
    title: 'Find Similar',
    desc: 'Finds semantically similar candidates by comparing AI-generated embeddings of skills, aesthetic style, and cultural metadata. Available on the INTELLIGENCE plan.',
  },
];

function ProfileGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 border border-border bg-[hsl(0,0%,98%)] hover:bg-white transition-colors group"
        data-testid="profile-guide-toggle"
      >
        <div className="flex items-center gap-2.5">
          <HelpCircle className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
          <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }}
            className="uppercase text-muted-foreground group-hover:text-foreground transition-colors">
            How to read this Intelligence Brief
          </span>
        </div>
        {open
          ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
          : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
        }
      </button>

      {open && (
        <div className="border border-t-0 border-border bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {GUIDE_STEPS.slice(0, 4).map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="px-5 py-5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                  <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                    className="text-foreground uppercase font-medium">{title}</p>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border border-t border-border">
            {GUIDE_STEPS.slice(4).map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="px-5 py-5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                  <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                    className="text-foreground uppercase font-medium">{title}</p>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
            <div className="px-5 py-5 bg-[hsl(0,0%,98%)]">
              <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                className="text-foreground uppercase font-medium mb-2">Pipeline Workflow</p>
              <div className="space-y-1.5">
                {[
                  { step: '1', label: 'Add to Shortlist', desc: 'Save for review' },
                  { step: '2', label: 'Contact via Messages', desc: 'Reach out on-platform' },
                  { step: '3', label: 'Track in Pipeline', desc: 'Manage hiring stages' },
                  { step: '4', label: 'Hired / Not a Fit', desc: 'Close the loop' },
                ].map(({ step, label, desc }) => (
                  <div key={step} className="flex items-start gap-2">
                    <span style={{ ...MONO, fontSize: '8px' }}
                      className="text-muted-foreground/50 w-3 flex-shrink-0 mt-0.5">{step}</span>
                    <div>
                      <span className="text-xs font-medium text-foreground">{label}</span>
                      <span className="text-xs text-muted-foreground ml-1.5">{desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-border bg-[hsl(0,0%,98%)]">
            <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.08em' }}
              className="text-muted-foreground/60 uppercase">
              FashionSpyder Intelligence Brief · Scores computed by FashionSpyder AI · Contact details protected per Flence privacy rules
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const CandidateProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animated, setAnimated] = useState(false);
  const [similarOpen, setSimilarOpen] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [inPipeline, setInPipeline] = useState(false);
  const [addingPipeline, setAddingPipeline] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [roleFit, setRoleFit] = useState(null);        // {role, score, reason, custom}
  const [roleFitLoading, setRoleFitLoading] = useState(false);
  const [targetRole, setTargetRole] = useState('');

  const isIntelligence = user?.subscription_plan === 'intelligence';

  const fetchRoleFit = (role) => {
    setRoleFitLoading(true);
    authFetch(`${BACKEND_URL}/api/candidates/${id}/role-fit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(role ? { role } : {}),
    })
      .then(r => r.json())
      .then(d => { if (d.success) setRoleFit({ role: d.role, score: d.score, reason: d.reason, breakdown: d.breakdown || [], confidence: d.confidence, custom: !!role }); })
      .catch(() => {})
      .finally(() => setRoleFitLoading(false));
  };

  // Fetch the candidate's best-fit role + contextual score (INTELLIGENCE)
  useEffect(() => {
    if (candidate && isIntelligence && !roleFit) fetchRoleFit(null);
  }, [candidate, isIntelligence]); // eslint-disable-line

  const handleScoreRole = () => {
    const role = targetRole.trim();
    if (!role || roleFitLoading) return;
    fetchRoleFit(role);
  };

  const resetRoleFit = () => { setTargetRole(''); fetchRoleFit(null); };

  useEffect(() => {
    authFetch(`${BACKEND_URL}/api/candidates/${id}`)
      .then(r => r.json())
      .then(d => { if (d.success) setCandidate(d.data); else setError('Candidate not found'); })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  // Fire profile_viewed event when authenticated HR user opens the brief
  useEffect(() => {
    if (id && user) {
      authFetch(`${BACKEND_URL}/api/candidates/${id}/track-view`, { method: 'POST' }).catch(() => {});
    }
  }, [id, user]); // eslint-disable-line

  // Trigger bar animations after data loads
  useEffect(() => {
    if (candidate) {
      const t = setTimeout(() => setAnimated(true), 400);
      return () => clearTimeout(t);
    }
  }, [candidate]);

  const handleFindSimilar = async () => {
    if (!user) { navigate('/login'); return; }
    if (user?.subscription_plan !== 'intelligence') {
      toast.info('Find Similar requires the INTELLIGENCE plan. Upgrade to unlock AI matchmaking.');
      return;
    }
    if (similar.length > 0) { setSimilarOpen(true); return; }
    setLoadingSimilar(true);
    setSimilarOpen(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/matchmaker/find-similar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: id, top_n: 8 }),
      });
      const data = await res.json();
      if (data.success) setSimilar(data.results || []);
      else toast.error('Could not load similar profiles');
    } catch { toast.error('Failed to load similar profiles'); }
    finally { setLoadingSimilar(false); }
  };

  const handleAddToShortlist = async () => {
    if (!user) { navigate('/login'); return; }
    if (addingPipeline || inPipeline) return;
    setAddingPipeline(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/dashboard/pipeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: id, stage: 'saved' }),
      });
      if (res.ok) {
        setInPipeline(true);
        toast.success(`${candidate?.full_name} added to your pipeline. Manage them in Pipeline →`);
      } else if (res.status === 401) {
        navigate('/login');
      } else {
        toast.error('Could not add to pipeline. Please try again.');
      }
    } catch {
      toast.error('Connection error. Please try again.');
    } finally {
      setAddingPipeline(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center space-y-4">
        <div className="w-6 h-6 border border-foreground border-t-transparent rounded-full animate-spin mx-auto" />
        <p style={{ ...MONO, fontSize: '10px', letterSpacing: '0.15em' }}
          className="text-muted-foreground uppercase">Retrieving Intelligence Brief</p>
      </div>
    </div>
  );

  if (error || !candidate) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">Candidate Not Found</h2>
        <Button onClick={() => navigate('/gallery')}>Back to Gallery</Button>
      </div>
    </div>
  );

  // ── Data prep ───────────────────────────────────────────────────────────────
  const cm = parseCultural(candidate.cultural_metadata);
  const skillDNA = computeSkillDNA(candidate);
  const culturalDNA = computeCulturalDNA(cm);
  const trendDNA = Math.round((candidate.trend_alignment || 0) * 100);

  const allHistorianTags = cm.historian_tags || [];
  const designerArchetypes = cm.designer_archetypes || [];
  const subcultureVibes = cm.subculture_vibe || [];
  const trendKeywords = (cm.trend_keywords || []).slice(0, 4);
  const styleDna = candidate.ai_style_dna || [];
  const portfolioTags = candidate.portfolio_tags || [];

  // Deduplicate cultural tags across all sources
  const seen = new Set();
  const deduped = (tags, variant) => tags.filter(t => {
    const key = t.toLowerCase().replace(/[-\s]/g, '');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const allCulturalTags = [...designerArchetypes, ...allHistorianTags];

  const softwareSkills = candidate.software_skills || [];
  const manualSkills = candidate.manual_creative_skills || [];
  const techStack = candidate.tech_stack || {};
  const workExp = candidate.work_experience || [];
  const education = candidate.education || [];
  const languages = candidate.languages || [];
  const portfolio = candidate.portfolio_preview || [];
  const stats = candidate.stats || {};
  const analytics = candidate.analytics_metrics || {};
  const perfStats = candidate.performance_stats || {};

  const authorityScore = analytics.authority_score || 0;
  const ranking = candidate.overall_ranking || 0;
  const nativeRole = cm.detected_role || [candidate.seniority, candidate.core_role].filter(Boolean).join(' ') || 'their core role';
  const futureTrendScore = cm.future_trend_score != null ? Math.round(cm.future_trend_score * 100) : null;

  const metaItems = [
    { icon: Briefcase, label: 'Experience', value: `${candidate.years_experience} yrs · ${candidate.seniority}` },
    { icon: Calendar, label: 'Availability', value: candidate.availability },
    { icon: MapPin, label: 'Relocation', value: candidate.willing_to_relocate ? 'Open to relocation' : 'No relocation' },
    { icon: Globe, label: 'Work Mode', value: (candidate.work_preferences || []).join(', ') },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ── NAV STRIP ─────────────────────────────────────────────────────── */}
      <div className="border-b border-border px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span style={{ ...MONO, fontSize: '10px', letterSpacing: '0.12em' }} className="uppercase">
              Back to Gallery
            </span>
          </button>
          <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.18em' }}
            className="text-muted-foreground/60 uppercase hidden sm:block">
            Intelligence Brief — FashionSpyder
          </span>
          <div className="flex items-center gap-2">
            {candidate.membership_level && (
              <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                className="border border-border text-muted-foreground px-2 py-0.5 uppercase">
                {candidate.membership_level}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── HERO PANEL ────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-border">

          {/* Photo Column */}
          <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-border relative overflow-hidden">
            <div className="aspect-[3/4] lg:aspect-auto lg:h-full min-h-[400px]">
              <img
                src={candidate.photo_url}
                alt={candidate.full_name}
                className="w-full h-full object-cover"
                style={{ filter: 'grayscale(0.15) contrast(1.03)' }}
                data-testid="candidate-photo"
              />
              {/* Overlay strip at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/30 to-transparent" />
              {authorityScore > 0 && (
                <div className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-white/95 px-2.5 py-1.5" data-testid="photo-authority-badge">
                  <BarChart2 className="w-3 h-3 text-foreground" strokeWidth={1.5} />
                  <span style={{ ...MONO, fontSize: '11px' }} className="font-medium text-foreground">
                    {authorityScore.toFixed(1)}
                  </span>
                  <span style={{ ...MONO, fontSize: '9px' }} className="text-muted-foreground">authority</span>
                </div>
              )}
            </div>
          </div>

          {/* Identity Column */}
          <div className="lg:col-span-8 flex flex-col">
            {/* Name + Role */}
            <div className="p-4 sm:p-8 border-b border-border">
              <p style={{ ...MONO, fontSize: '10px', letterSpacing: '0.15em' }}
                className="text-muted-foreground uppercase mb-3">
                {candidate.seniority} · {candidate.core_role}
              </p>
              <h1 style={{ ...SERIF, fontSize: 'clamp(2.5rem, 5vw, 4rem)', lineHeight: 1.02, letterSpacing: '-0.02em' }}
                className="font-semibold text-foreground mb-2" data-testid="candidate-name">
                {candidate.full_name}
              </h1>
              {candidate.secondary_role && (
                <p className="text-muted-foreground text-sm mt-1">{candidate.secondary_role}</p>
              )}
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-3">
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span>{candidate.location_city}, {candidate.location_country}</span>
              </div>
            </div>

            {/* ── ROLE FIT (contextual) + supporting scores ──────────────── */}
            <div className="border-b border-border" data-testid="score-readouts">
              {/* Role Fit band */}
              <div className="px-4 sm:px-8 py-5 sm:py-6 border-b border-border" data-testid="role-fit-band">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center">
                    <Target className="w-3.5 h-3.5 text-[hsl(var(--primary))] mr-2" strokeWidth={1.5} />
                    <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }} className="uppercase text-muted-foreground">
                      Role Fit{roleFit?.custom ? '' : ' · Best Fit'}
                    </p>
                    <ScoreTooltip label="Role Fit" />
                  </div>
                  {isIntelligence && roleFit?.custom && (
                    <button onClick={resetRoleFit}
                      className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="role-fit-reset">
                      <RotateCcw className="w-3 h-3" strokeWidth={1.5} />
                      <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }} className="uppercase">Best fit</span>
                    </button>
                  )}
                </div>

                <div className="flex items-end gap-3">
                  {(isIntelligence && roleFitLoading && !roleFit) ? (
                    <div className="w-7 h-7 border border-foreground border-t-transparent rounded-full animate-spin" data-testid="role-fit-loading" />
                  ) : (
                    <span style={{ ...SERIF, fontSize: 'clamp(2rem, 6vw, 3.25rem)', lineHeight: 0.9 }}
                      className="font-semibold text-foreground" data-testid="role-fit-score">
                      {isIntelligence
                        ? (roleFit ? roleFit.score.toFixed(1) : authorityScore.toFixed(1))
                        : authorityScore.toFixed(1)}
                      <span style={{ ...MONO, fontSize: '12px' }} className="text-muted-foreground ml-0.5">/10</span>
                    </span>
                  )}
                  <p className="text-sm text-muted-foreground mb-1.5">
                    as <span className="text-foreground font-medium" data-testid="role-fit-role">
                      {isIntelligence && roleFit ? roleFit.role : nativeRole}
                    </span>
                  </p>
                </div>

                {isIntelligence && roleFit?.reason && (
                  <p className="text-xs text-muted-foreground mt-3 italic border-l-2 border-[hsl(var(--primary))] pl-3 leading-relaxed"
                    data-testid="role-fit-reason">
                    {roleFit.reason}
                  </p>
                )}

                {/* Transparent rubric breakdown — shows the criteria behind the score */}
                {isIntelligence && roleFit?.breakdown?.length > 0 && (
                  <div className="mt-4" data-testid="role-fit-breakdown">
                    <div className="flex items-center justify-between mb-2">
                      <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.14em' }} className="uppercase text-muted-foreground/70">
                        How this score is built
                      </p>
                      {roleFit.confidence && (
                        <span style={{ ...MONO, fontSize: '8px', letterSpacing: '0.1em' }}
                          className={`uppercase px-1.5 py-0.5 border ${roleFit.confidence === 'high' ? 'border-[hsl(var(--primary)/0.4)] text-[hsl(var(--primary))]' : 'border-border text-muted-foreground'}`}
                          data-testid="role-fit-confidence">
                          {roleFit.confidence} confidence
                        </span>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {roleFit.breakdown.map((b) => (
                        <div key={b.key} className="flex items-center gap-2" title={b.evidence} data-testid={`role-fit-crit-${b.key}`}>
                          <span style={{ ...MONO, fontSize: '9px' }} className="text-muted-foreground w-28 flex-shrink-0 truncate">{b.label}</span>
                          <div className="flex-1 h-1 bg-secondary overflow-hidden">
                            <div className="h-full bg-foreground" style={{ width: `${Math.max(2, b.score * 10)}%` }} />
                          </div>
                          <span style={{ ...MONO, fontSize: '9px' }} className="text-foreground w-7 text-right flex-shrink-0">{b.score.toFixed(1)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Score for a role */}
                {isIntelligence ? (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 border border-border focus-within:border-foreground transition-colors bg-white max-w-lg">
                      <input
                        value={targetRole}
                        onChange={e => setTargetRole(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleScoreRole(); }}
                        placeholder="Score for a role — e.g. Stylist at Gucci, Creative Director…"
                        className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none placeholder:text-muted-foreground/60"
                        data-testid="role-fit-input"
                      />
                      <button
                        onClick={handleScoreRole}
                        disabled={roleFitLoading || !targetRole.trim()}
                        className="px-3.5 py-2.5 text-foreground hover:bg-secondary transition-colors disabled:opacity-30"
                        data-testid="role-fit-submit"
                      >
                        {roleFitLoading ? (
                          <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" strokeWidth={1.5} />
                        )}
                      </button>
                    </div>
                    <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.08em' }}
                      className="text-muted-foreground/50 uppercase mt-2">
                      Scores adapt to the role, seniority & skills you need
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] hover:underline"
                    data-testid="role-fit-upgrade"
                  >
                    <Zap className="w-3 h-3" strokeWidth={1.5} />
                    Score this candidate for any role with INTELLIGENCE
                  </button>
                )}
              </div>

              {/* Supporting intrinsic scores */}
              <div className="grid grid-cols-2 divide-x divide-border">
                {[
                  { label: 'Trend Align.', value: `${trendDNA}`, suffix: '%', icon: TrendingUp, testid: 'score-trend' },
                  { label: 'Authority', value: authorityScore.toFixed(1), suffix: '', icon: BarChart2, testid: 'score-authority' },
                ].map(({ label, value, suffix, icon: Icon, testid }) => (
                  <div key={label} className="p-3 sm:p-5 flex flex-col items-center justify-center text-center" data-testid={testid}>
                    <Icon className="w-4 h-4 text-muted-foreground mb-2" strokeWidth={1.5} />
                    <div className="flex items-baseline gap-0.5">
                      <span style={{ ...SERIF, fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', lineHeight: 1 }} className="font-semibold text-foreground">
                        {value}
                      </span>
                      <span style={{ ...MONO, fontSize: '10px' }} className="text-muted-foreground">{suffix}</span>
                    </div>
                    <div className="flex items-center justify-center mt-1.5">
                      <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }}
                        className="text-muted-foreground uppercase">{label}</p>
                      <ScoreTooltip label={label} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Meta */}
            <div className="grid grid-cols-2 gap-0 divide-y divide-x divide-border border-b border-border flex-1">
              {metaItems.map(({ icon: Icon, label, value }) => (
                <div key={label} className="p-4 flex items-start gap-3">
                  <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                      className="text-muted-foreground/70 uppercase mb-0.5">{label}</p>
                    <p className="text-xs text-foreground font-medium">{value || '—'}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="p-4 sm:p-6 flex items-center gap-3 flex-wrap">
              <Button
                className="bg-foreground text-background hover:bg-foreground/80 rounded-none px-6"
                onClick={() => navigate(`/dashboard/messages?candidate=${candidate.id}`)}
                data-testid="contact-btn"
              >
                Contact Candidate
              </Button>
              <Button
                variant="outline"
                className={`rounded-none gap-2 transition-all ${inPipeline ? 'bg-foreground text-background border-foreground hover:bg-foreground/80' : ''}`}
                onClick={inPipeline ? () => navigate('/dashboard/pipeline') : handleAddToShortlist}
                disabled={addingPipeline}
                data-testid="shortlist-btn"
              >
                {addingPipeline ? (
                  <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin" />
                ) : inPipeline ? (
                  <Check className="w-3.5 h-3.5" strokeWidth={2} />
                ) : (
                  <BookmarkPlus className="w-3.5 h-3.5" strokeWidth={1.5} />
                )}
                {inPipeline ? 'Go to Pipeline' : addingPipeline ? 'Adding...' : 'Add to Shortlist'}
              </Button>
              {user && (
                <Button
                  variant="outline"
                  className="rounded-none gap-2 border-foreground/30 hover:border-foreground"
                  onClick={handleFindSimilar}
                  data-testid="find-similar-btn"
                >
                  <ScanSearch className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Find Similar
                  {user?.subscription_plan !== 'intelligence' && (
                    <span style={{ ...MONO, fontSize: '8px' }} className="text-muted-foreground/60 uppercase ml-0.5">Intelligence</span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── AI ANALYST — PROFILE SNAPSHOT + ASK AI ────────────────────────── */}
      <AIBrief
        candidateId={candidate.id}
        candidateName={candidate.full_name}
        isIntelligence={user?.subscription_plan === 'intelligence'}
      />

      {/* ── AI DNA INTELLIGENCE PANEL ─────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-0">
        {user?.subscription_plan === 'intelligence' ? (
          <div className="border border-t-0 border-border bg-[hsl(0,0%,98%)]" data-testid="dna-panel">
            {/* Header */}
            <div className="border-b border-border px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-4 h-4 text-foreground" strokeWidth={1.5} />
                <span style={{ ...MONO, fontSize: '10px', letterSpacing: '0.18em' }}
                  className="uppercase text-foreground font-medium">AI DNA Intelligence</span>
              </div>
              <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                className="text-muted-foreground uppercase hidden sm:block">
                Skill · Cultural · Trend Vectors
              </span>
            </div>

            {/* Three DNA Bars */}
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
              <div className="px-4 sm:px-8 py-5 sm:py-7">
                <DNABar
                  label="Skill DNA"
                  score={skillDNA}
                  sublabel={`${softwareSkills.length + manualSkills.length} skills mapped`}
                  icon={Layers}
                  animate={animated}
                />
              </div>
              <div className="px-4 sm:px-8 py-5 sm:py-7">
                <DNABar
                  label="Cultural DNA"
                  score={culturalDNA}
                  sublabel={`${allCulturalTags.length} archetypes analysed`}
                  icon={Users}
                  animate={animated}
                />
              </div>
              <div className="px-4 sm:px-8 py-5 sm:py-7">
                <DNABar
                  label="Trend DNA"
                  score={trendDNA}
                  sublabel={futureTrendScore != null ? `Future forecast: ${futureTrendScore}%` : 'Pinterest · Google · BoF'}
                  icon={TrendingUp}
                  animate={animated}
                />
              </div>
            </div>

            {/* Cultural Signature */}
            {(allCulturalTags.length > 0 || styleDna.length > 0 || portfolioTags.length > 0) && (
              <div className="border-t border-border px-4 sm:px-8 py-5 sm:py-6">
                <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }}
                  className="text-muted-foreground uppercase mb-4">Cultural Signature</p>
                <div className="flex flex-wrap gap-2">
                  {deduped(designerArchetypes).map((tag, i) => (
                    <CulturalChip key={`arch-${i}`} tag={tag} variant="primary" />
                  ))}
                  {deduped(allHistorianTags).map((tag, i) => (
                    <CulturalChip key={`hist-${i}`} tag={tag} variant="secondary" />
                  ))}
                  {deduped(styleDna).map((tag, i) => (
                    <CulturalChip key={`style-${i}`} tag={tag} variant="secondary" />
                  ))}
                  {deduped(portfolioTags).map((tag, i) => (
                    <CulturalChip key={`port-${i}`} tag={tag} variant="gold" />
                  ))}
                  {deduped(subcultureVibes).map((tag, i) => (
                    <CulturalChip key={`sub-${i}`} tag={tag} variant="secondary" />
                  ))}
                  {deduped(trendKeywords).map((tag, i) => (
                    <CulturalChip key={`trend-${i}`} tag={tag} variant="gold" />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* RECON — locked DNA panel teaser */
          <div className="border border-t-0 border-border" data-testid="dna-panel-locked">
            <div className="border-b border-border px-8 py-5 flex items-center justify-between bg-[hsl(0,0%,98%)]">
              <div className="flex items-center gap-3">
                <Zap className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <span style={{ ...MONO, fontSize: '10px', letterSpacing: '0.18em' }}
                  className="uppercase text-muted-foreground">AI DNA Intelligence</span>
              </div>
              <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                className="text-muted-foreground/50 uppercase hidden sm:block border border-border px-2 py-0.5">
                INTELLIGENCE
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
              {['Skill DNA', 'Cultural DNA', 'Trend DNA'].map((label) => (
                <div key={label} className="px-8 py-7 relative">
                  <div className="space-y-3 opacity-20 pointer-events-none select-none">
                    <div className="flex items-center justify-between">
                      <span style={{ ...MONO, fontSize: '10px', letterSpacing: '0.12em' }}
                        className="uppercase text-muted-foreground">{label}</span>
                      <span style={{ ...MONO, fontSize: '22px' }} className="font-medium text-foreground leading-none">
                        ——<span style={{ fontSize: '11px' }} className="text-muted-foreground ml-0.5">%</span>
                      </span>
                    </div>
                    <div className="h-px bg-border" />
                  </div>
                </div>
              ))}
            </div>
            <div className="px-8 py-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Unlock the full AI DNA breakdown</p>
                <p className="text-sm text-muted-foreground">
                  Skill, Cultural and Trend vectors — plus Cultural Signature tags — are available on the INTELLIGENCE plan.
                </p>
              </div>
              <Button
                onClick={() => navigate('/pricing')}
                className="rounded-none bg-foreground text-background hover:bg-foreground/80 flex-shrink-0 px-6"
                data-testid="dna-upgrade-btn"
              >
                View Plans
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── INTELLIGENCE BRIEF GUIDE ──────────────────────────────────────── */}
      <ProfileGuide />

      {/* ── MAIN CONTENT ──────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Left: Primary Content */}
          <div className="lg:col-span-8 space-y-8">

            {/* About */}
            <section className="border border-border p-8" data-testid="about-section">
              <SectionLabel>About</SectionLabel>
              <p className="text-foreground leading-relaxed mt-4">
                {candidate.optimized_bio}
              </p>
              {languages.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-border">
                  {languages.map((l, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-foreground">{l.language}</span>
                      <span style={{ ...MONO, fontSize: '9px' }} className="text-muted-foreground">
                        {l.level}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Skills */}
            {(softwareSkills.length > 0 || manualSkills.length > 0 || Object.keys(techStack).length > 0) && (
              <section className="border border-border" data-testid="skills-section">
                <div className="px-8 py-5 border-b border-border">
                  <SectionLabel>Skills</SectionLabel>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                  {softwareSkills.length > 0 && (
                    <div className="px-8 py-6">
                      <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }}
                        className="text-muted-foreground uppercase mb-4">Software</p>
                      {softwareSkills.map((s, i) => (
                        <SkillRow key={i} name={s.name} level={s.level} animate={animated} />
                      ))}
                      {/* Tech stack */}
                      {Object.entries(techStack).map(([name, level], i) => (
                        <SkillRow key={`ts-${i}`} name={name} level={level} animate={animated} />
                      ))}
                    </div>
                  )}
                  {manualSkills.length > 0 && (
                    <div className="px-8 py-6">
                      <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }}
                        className="text-muted-foreground uppercase mb-4">Craft & Creative</p>
                      {manualSkills.map((s, i) => (
                        <SkillRow key={i} name={s.name} level={s.level} animate={animated} />
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Experience */}
            {workExp.length > 0 && (
              <section className="border border-border" data-testid="experience-section">
                <div className="px-8 py-5 border-b border-border">
                  <SectionLabel>Experience</SectionLabel>
                </div>
                <div className="px-8 py-6">
                  {workExp.map((exp, i) => (
                    <div key={i} className="flex gap-6 mb-8 last:mb-0">
                      {/* Timeline indicator */}
                      <div className="flex flex-col items-center pt-1 flex-shrink-0 w-4">
                        <div className="w-1.5 h-1.5 bg-foreground flex-shrink-0" />
                        {i < workExp.length - 1 && (
                          <div className="w-px flex-1 bg-border mt-2" style={{ minHeight: '48px' }} />
                        )}
                      </div>
                      <div className="pb-2 flex-1">
                        <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
                          <h3 className="text-sm font-semibold text-foreground">{exp.title}</h3>
                          <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }}
                            className="text-muted-foreground/70 flex-shrink-0">{exp.years || exp.period}</span>
                        </div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">{exp.company}</p>
                        {exp.description && (
                          <p className="text-sm text-muted-foreground leading-relaxed">{exp.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Education */}
            {education.length > 0 && (
              <section className="border border-border" data-testid="education-section">
                <div className="px-8 py-5 border-b border-border">
                  <SectionLabel>Education</SectionLabel>
                </div>
                <div className="px-8 py-6 space-y-5">
                  {education.map((edu, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <GraduationCap className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" strokeWidth={1.5} />
                      <div>
                        <p className="text-sm font-semibold text-foreground">{edu.degree}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{edu.institution}</p>
                        {edu.field_of_study && (
                          <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }}
                            className="text-muted-foreground/60 uppercase mt-0.5">{edu.field_of_study}</p>
                        )}
                        <p style={{ ...MONO, fontSize: '9px' }} className="text-muted-foreground/60 mt-0.5">{edu.years}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right: Sidebar */}
          <div className="lg:col-span-4 space-y-6">

            {/* Portfolio — compact sidebar preview, click opens full lightbox */}
            {portfolio.length > 0 && (
              <section data-testid="portfolio-section">
                <div className="flex items-center justify-between mb-3">
                  <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }}
                    className="text-muted-foreground uppercase">Portfolio</p>
                  <button
                    onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                    style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }}
                    className="text-muted-foreground/60 uppercase hover:text-foreground transition-colors flex items-center gap-1"
                    data-testid="portfolio-view-all-btn"
                  >
                    <Maximize2 className="w-3 h-3" strokeWidth={1.5} />
                    View all {portfolio.length}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-0.5">
                  {portfolio.slice(0, 6).map((item, i) => (
                    <button
                      key={i}
                      onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                      className="aspect-square overflow-hidden bg-secondary relative group"
                      data-testid={`portfolio-thumb-${i}`}
                    >
                      {isVideo(item.cover_image) ? (
                        <div className="w-full h-full bg-zinc-900 flex flex-col items-center justify-center gap-1">
                          <div className="w-7 h-7 rounded-full border border-white/30 group-hover:border-white/60 flex items-center justify-center transition-colors">
                            <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white/60 group-hover:fill-white ml-0.5 transition-colors">
                              <polygon points="5,3 19,12 5,21" />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <>
                          <img
                            src={item.cover_image}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            style={{ filter: 'grayscale(0.3)' }}
                            onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
                          />
                          <div className="w-full h-full bg-[hsl(0,0%,93%)] items-center justify-center hidden">
                            <ImageOff className="w-4 h-4 text-muted-foreground/30" strokeWidth={1} />
                          </div>
                        </>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center">
                        <Maximize2 className="w-3 h-3 text-white opacity-0 group-hover:opacity-80 transition-opacity" strokeWidth={1.5} />
                      </div>
                    </button>
                  ))}
                </div>
                {portfolio.length > 6 && (
                  <button
                    onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                    style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }}
                    className="w-full mt-1 py-2 border border-border text-muted-foreground uppercase hover:bg-secondary transition-colors"
                  >
                    +{portfolio.length - 6} more projects
                  </button>
                )}
              </section>
            )}

            {/* Platform Stats */}
            <section className="border border-border" data-testid="platform-stats">
              <div className="px-5 py-3 border-b border-border">
                <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }}
                  className="text-muted-foreground uppercase">Platform Stats</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-y divide-border">
                {[
                  { label: 'Profile Views', value: (analytics.total_portfolio_views || stats.profile_views || 0).toLocaleString(), icon: Eye },
                  { label: 'Endorsements', value: stats.endorsements || 0, icon: Award },
                  { label: 'Wishlist', value: analytics.wishlist_count || 0, icon: Star },
                  { label: 'Projects', value: stats.completed_projects || 0, icon: Layers },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="p-4 flex flex-col items-center justify-center text-center">
                    <span style={{ ...SERIF, fontSize: '1.5rem', lineHeight: 1 }}
                      className="font-semibold text-foreground">{value}</span>
                    <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.1em' }}
                      className="text-muted-foreground uppercase mt-1">{label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* CV only — social links hidden to keep communication on-platform */}
            {candidate.cv_url && (
              <section className="border border-border" data-testid="social-links">
                <div className="px-5 py-3 border-b border-border">
                  <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }}
                    className="text-muted-foreground uppercase">Documents</p>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <CvViewerButton candidateId={candidate.id} />
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                    <Shield className="w-3 h-3 flex-shrink-0 text-[hsl(var(--primary))]" />
                    Contact details are protected. Use Messages to connect.
                  </p>
                </div>
              </section>
            )}

            {/* Employment Preferences */}
            <section className="border border-border" data-testid="preferences-section">
              <div className="px-5 py-3 border-b border-border">
                <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }}
                  className="text-muted-foreground uppercase">Preferences</p>
              </div>
              <div className="px-5 py-4 space-y-2">
                {(candidate.employment_types || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-foreground flex-shrink-0" />
                    <span className="text-xs text-foreground">{t}</span>
                  </div>
                ))}
                {(candidate.work_preferences || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-border flex-shrink-0" />
                    <span className="text-xs text-muted-foreground">{t}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* ── FULL PORTFOLIO SECTION ────────────────────────────────────────── */}
      {portfolio.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <PortfolioSection
            portfolio={portfolio}
            onOpenLightbox={(i) => { setLightboxIndex(i); setLightboxOpen(true); }}
          />
        </div>
      )}

      {/* ── PORTFOLIO LIGHTBOX ────────────────────────────────────────────── */}
      {lightboxOpen && portfolio.length > 0 && (
        <PortfolioLightbox
          projects={portfolio}
          initialProjectIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* ── FIND SIMILAR PANEL ────────────────────────────────────────────── */}
      {similarOpen && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="similar-panel">
          <div className="border border-border border-t-0">
            <div className="px-8 py-5 border-b border-border flex items-center justify-between bg-[hsl(0,0%,98%)]">
              <div>
                <p style={{ ...MONO, fontSize: '10px', letterSpacing: '0.15em' }}
                  className="uppercase text-foreground font-medium">Similar Intelligence Profiles</p>
                <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }}
                  className="text-muted-foreground mt-0.5">Semantically matched · {candidate.full_name}</p>
              </div>
              <button onClick={() => setSimilarOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>

            {loadingSimilar ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-border">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[3/4] bg-secondary" />
                    <div className="p-4 space-y-2">
                      <div className="h-2.5 bg-secondary w-3/4" />
                      <div className="h-2 bg-secondary w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : similar.length === 0 ? (
              <p style={{ ...MONO, fontSize: '10px' }}
                className="text-muted-foreground py-12 text-center uppercase tracking-widest">
                No similar profiles found
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-border">
                {similar.map(c => (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/candidate/${c.id}`)}
                    className="text-left group hover:bg-[hsl(0,0%,98%)] transition-colors"
                    data-testid={`similar-card-${c.id}`}
                  >
                    <div className="aspect-[3/4] overflow-hidden bg-secondary">
                      {c.photo_url ? (
                        <img src={c.photo_url} alt={c.full_name}
                          className="w-full h-full object-cover transition-all duration-500"
                          style={{ filter: 'grayscale(0.4)' }}
                          onMouseEnter={e => { e.currentTarget.style.filter = 'grayscale(0)'; }}
                          onMouseLeave={e => { e.currentTarget.style.filter = 'grayscale(0.4)'; }}
                        />
                      ) : <div className="w-full h-full bg-secondary" />}
                    </div>
                    <div className="p-4 border-t border-border">
                      <p style={SERIF} className="text-sm font-semibold truncate">{c.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {[c.seniority, c.core_role].filter(Boolean).join(' ')}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }}
                          className="text-muted-foreground">
                          {c.similarity_score?.toFixed(0)}% match
                        </span>
                        <ChevronRight className="w-3 h-3 text-muted-foreground/40 group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateProfile;
